import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import StructuredData from '@/components/base/StructuredData';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    phone_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9opb1gsenbniolesj90';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '', phone_alt: '' });
      return;
    }
    setFormStatus('submitting');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '', phone_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const teamMembers = [
    {
      name: '林小雨',
      role: '创始人 & CEO',
      image: 'https://readdy.ai/api/search-image?query=Professional%20anime%20style%20portrait%20of%20a%20young%20Chinese%20woman%20with%20warm%20smile%2C%20clean%20light%20background%2C%20modern%20casual%20attire%2C%20soft%20lighting%2C%20friendly%20professional%20look&width=200&height=200&seq=team-01&orientation=squarish',
      desc: '资深漫画爱好者，10年互联网产品经验',
    },
    {
      name: '陈浩',
      role: '内容总监',
      image: 'https://readdy.ai/api/search-image?query=Professional%20anime%20style%20portrait%20of%20a%20young%20Chinese%20man%20with%20glasses%2C%20friendly%20expression%2C%20clean%20light%20background%2C%20modern%20casual%20style%2C%20soft%20lighting&width=200&height=200&seq=team-02&orientation=squarish',
      desc: '前知名漫画平台主编，深耕漫画行业8年',
    },
    {
      name: '苏瑾',
      role: '技术总监',
      image: 'https://readdy.ai/api/search-image?query=Professional%20anime%20style%20portrait%20of%20a%20young%20Chinese%20woman%20in%20tech%20setting%2C%20confident%20look%2C%20clean%20light%20background%2C%20modern%20style%2C%20soft%20lighting&width=200&height=200&seq=team-03&orientation=squarish',
      desc: '全栈工程师，热爱用技术提升阅读体验',
    },
    {
      name: '王大锤',
      role: '运营总监',
      image: 'https://readdy.ai/api/search-image?query=Professional%20anime%20style%20portrait%20of%20a%20young%20Chinese%20man%20with%20energetic%20smile%2C%20clean%20light%20background%2C%20modern%20casual%20look%2C%20soft%20lighting&width=200&height=200&seq=team-04&orientation=squarish',
      desc: '社群运营专家，拥有百万粉丝运营经验',
    },
  ];

  const testimonials = [
    {
      name: '漫画迷小王',
      avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20avatar%20of%20a%20young%20person%2C%20simple%20clean%20style%2C%20neutral%20background%2C%20friendly%20expression%2C%20soft%20lighting&width=100&height=100&seq=avatar-01&orientation=squarish',
      text: '樱花动漫91真的是我见过最好用的漫画阅读平台！下拉式阅读太方便了，不用一直翻页，一口气看了好多话。而且漫画种类很全，更新也快。',
      rating: 5,
    },
    {
      name: '二次元少女',
      avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20avatar%20of%20a%20young%20girl%2C%20simple%20clean%20style%2C%20neutral%20background%2C%20cute%20expression%2C%20soft%20lighting&width=100&height=100&seq=avatar-02&orientation=squarish',
      text: '从朋友那里知道的这个网站，现在每天都在追更。画质清晰，加载也快，最关键的是完全免费！推荐给了好多朋友。',
      rating: 5,
    },
    {
      name: '资深漫友',
      avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20avatar%20of%20a%20young%20man%2C%20simple%20clean%20style%2C%20neutral%20background%2C%20calm%20expression%2C%20soft%20lighting&width=100&height=100&seq=avatar-03&orientation=squarish',
      text: '看了十几年漫画了，樱花动漫91的资源真的很丰富。分类也很细致，想找什么类型的漫画都很方便。最爱它的排行榜功能。',
      rating: 5,
    },
    {
      name: '追漫达人',
      avatar: 'https://readdy.ai/api/search-image?query=Anime%20style%20portrait%20avatar%20of%20a%20young%20woman%2C%20simple%20clean%20style%2C%20neutral%20background%2C%20bright%20expression%2C%20soft%20lighting&width=100&height=100&seq=avatar-04&orientation=squarish',
      text: '更新速度没话说！热门漫画几乎和日本同步更新，再也不用到处找资源了。樱花动漫91就是我的一站式漫画阅读站。',
      rating: 4,
    },
  ];

  return (
    <>
      <SEOMeta
        title="关于我们 - 樱花动漫91品牌介绍、口碑与联系方式"
        description="樱花动漫91致力于为漫画爱好者提供优质的在线漫画阅读体验。了解我们的品牌故事、用户口碑评价，以及联系方式。樱花动漫91漫画全集下拉式阅读平台。"
        keywords="樱花动漫91,关于我们,漫画平台,品牌介绍,用户口碑,联系方式"
        canonicalUrl="https://yinghuadongman91.com/about"
      />
      <StructuredData
        type="WebPage"
        data={{
          name: '关于我们 - 樱花动漫91',
          description: '樱花动漫91品牌介绍、口碑与联系方式',
          url: 'https://yinghuadongman91.com/about',
        }}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* 品牌介绍 */}
            <section className="bg-white rounded-xl p-6 md:p-10 mb-8" aria-labelledby="brand-heading">
              <h1 id="brand-heading" className="font-heading text-3xl text-foreground-950 mb-6">
                关于樱花动漫91
              </h1>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-lg font-medium text-foreground-950 mb-4">我们的故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    樱花动漫91成立于2024年，由一群热爱漫画的年轻人创立。我们深知漫画爱好者在寻找优质阅读资源时的困扰——广告繁多、加载缓慢、资源不全。因此，我们立志打造一个简洁、流畅、资源丰富的在线漫画阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们坚信，好看的漫画应该被更多人看到，优质的阅读体验不应该被广告和付费门槛所阻碍。樱花动漫91始终秉持<strong>免费、优质、便捷</strong>的理念，为漫画爱好者提供漫画全集下拉式阅读体验。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    目前，平台已收录超过上万部漫画作品，涵盖恋爱、热血、奇幻、搞笑、恐怖、校园、冒险、悬疑等丰富分类。每日更新最新漫画章节，确保读者能够第一时间看到最热门的漫画内容。
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {[
                    { num: '10000+', label: '漫画作品' },
                    { num: '500万+', label: '月活跃用户' },
                    { num: '99.9%', label: '服务可用率' },
                    { num: '8', label: '漫画分类' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="bg-background-50 rounded-xl p-5 text-center"
                    >
                      <div className="font-heading text-2xl text-primary-500 mb-1">{stat.num}</div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* 团队 */}
            <section className="bg-white rounded-xl p-6 md:p-10 mb-8" aria-labelledby="team-heading">
              <h2 id="team-heading" className="font-heading text-2xl text-foreground-950 mb-6">
                核心团队
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {teamMembers.map((member) => (
                  <div key={member.name} className="text-center">
                    <div className="w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        title={`${member.name} - ${member.role}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 whitespace-nowrap">{member.name}</h3>
                    <p className="text-xs text-primary-500 mt-0.5 whitespace-nowrap">{member.role}</p>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 口碑 */}
            <section id="reputation" className="bg-white rounded-xl p-6 md:p-10 mb-8" aria-labelledby="reputation-heading">
              <h2 id="reputation-heading" className="font-heading text-2xl text-foreground-950 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {testimonials.map((t) => (
                  <div key={t.name} className="bg-background-50 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-950 whitespace-nowrap">{t.name}</p>
                        <div className="flex items-center gap-0.5 mt-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="w-3 h-3 flex items-center justify-center">
                              <i className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.text}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* 联系方式 */}
            <section id="contact" className="bg-white rounded-xl p-6 md:p-10 mb-14" aria-labelledby="contact-heading">
              <h2 id="contact-heading" className="font-heading text-2xl text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们，或直接填写右侧表单，我们会在24小时内回复您。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm text-foreground-950">contact@yinghuadongman91.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-lg text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">客服电话</p>
                        <p className="text-sm text-foreground-950">400-888-9191</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-lg text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">公司地址</p>
                        <p className="text-sm text-foreground-950">上海市浦东新区张江高科技园区樱花路91号</p>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">
                        {contactForm.message.length}/500
                      </p>
                    </div>

                    <div className="honeypot-wrapper" aria-hidden="true">
                      <label htmlFor="phone_alt">Phone</label>
                      <input
                        id="phone_alt"
                        name="phone_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        value={contactForm.phone_alt}
                        onChange={(e) => setContactForm({ ...contactForm, phone_alt: e.target.value })}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-white rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '提交失败，请稍后重试'}</p>
                    )}
                  </div>
                </form>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}